import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { EvalApiService } from '../../services/EvalApiService';
import { EvalGtDataset, EvalModel, EvalRun, RunType } from '../../types';
import { fmtIso } from '../../utils';

const RUN_TYPES: RunType[] = ['Full', 'OD', 'PR', 'PC'];

// Which run types can serve as base run input for a given run type
const COMPATIBLE_SOURCE_TYPES: Record<'PR' | 'PC', RunType[]> = {
  PR: ['OD', 'Full'],
  PC: ['PR', 'Full'],
};

// The model ID most relevant to the next stage: OD output feeds PR; PR output feeds PC
function keyModelId(run: EvalRun, forRunType: 'PR' | 'PC'): string | null {
  return forRunType === 'PR' ? run.model_ids.OD : run.model_ids.PR;
}

function baseRunLabel(run: EvalRun, forRunType: 'PR' | 'PC'): string {
  const date = fmtIso(run.triggered_at);
  const model = keyModelId(run, forRunType);
  return `${date} · [${run.run_type}]${model ? ` · ${model}` : ''}`;
}

export const RunSchedulerPage: React.FC = () => {
  const { token } = useAuthContext();
  const navigate = useNavigate();

  const [models, setModels] = useState<EvalModel[]>([]);
  const [datasets, setDatasets] = useState<EvalGtDataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [runType, setRunType] = useState<RunType>('Full');
  const [odModelId, setOdModelId] = useState<string>('');
  const [prModelId, setPrModelId] = useState<string>('');
  const [pcModelId, setPcModelId] = useState<string>('');
  const [datasetId, setDatasetId] = useState<string>('');
  const [configJson, setConfigJson] = useState<string>('');

  // Base run state (only relevant for standalone PR and PC run types)
  const [useBaseRun, setUseBaseRun] = useState(false);
  const [baseRunId, setBaseRunId] = useState<string>('');
  const [baseRuns, setBaseRuns] = useState<EvalRun[]>([]);
  const [baseRunsLoading, setBaseRunsLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      EvalApiService.listModels(token ?? ''),
      EvalApiService.listDatasets(token ?? ''),
    ]).then(([{ items: m }, { items: d }]) => {
      setModels(m);
      const sorted = [...d].sort((a, b) => {
        if (a.status === 'locked' && b.status !== 'locked') return -1;
        if (a.status !== 'locked' && b.status === 'locked') return 1;
        return 0;
      });
      setDatasets(sorted);
      setLoading(false);
    });
  }, [token]);

  // Reset base run state when dataset or run type changes
  useEffect(() => {
    setUseBaseRun(false);
    setBaseRunId('');
    setBaseRuns([]);
  }, [datasetId, runType]);

  // Load eligible base runs when the toggle is turned on
  useEffect(() => {
    if (!useBaseRun || !datasetId || (runType !== 'PR' && runType !== 'PC')) return;
    setBaseRunsLoading(true);
    setBaseRunId('');
    const compatibleTypes = COMPATIBLE_SOURCE_TYPES[runType];
    EvalApiService.listRuns(token ?? '', { dataset_id: datasetId, status: 'completed' }).then(
      ({ items }) => {
        setBaseRuns(items.filter((r) => compatibleTypes.includes(r.run_type)));
        setBaseRunsLoading(false);
      }
    );
  }, [useBaseRun, datasetId, runType, token]);

  const selectedDataset = datasets.find((d) => d.dataset_id === datasetId);
  const isDraft = selectedDataset?.status === 'draft';

  const showOD = runType === 'Full' || runType === 'OD';
  const showPR = runType === 'Full' || runType === 'PR';
  const showPC = runType === 'Full' || runType === 'PC';
  // Toggle is only available for standalone PR and PC runs
  const showBaseRunToggle = runType === 'PR' || runType === 'PC';

  const odModels = models.filter(
    (m) => m.model_type === 'OD' && m.deployment_status !== 'archived'
  );
  const prModels = models.filter(
    (m) => m.model_type === 'PR' && m.deployment_status !== 'archived'
  );
  const pcModels = models.filter(
    (m) => m.model_type === 'PC' && m.deployment_status !== 'archived'
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!datasetId) return;
    setSubmitting(true);
    try {
      let parsedConfig: Record<string, unknown> = {};
      if (configJson.trim()) {
        parsedConfig = JSON.parse(configJson);
      }
      const newRun = await EvalApiService.scheduleRun(token ?? '', {
        run_type: runType,
        model_ids: {
          OD: showOD ? odModelId || null : null,
          PR: showPR ? prModelId || null : null,
          PC: showPC ? pcModelId || null : null,
        },
        dataset_id: datasetId,
        source_run_id: showBaseRunToggle && useBaseRun ? baseRunId || null : null,
        config: parsedConfig,
      });
      navigate(`/eval/runs/${newRun.run_id}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="eval-page-header">
        <div>
          <div className="eval-page-title">Schedule Evaluation Run</div>
          <div className="eval-page-sub">
            Configure and launch a new evaluation run against a ground-truth dataset.
          </div>
        </div>
      </div>

      <form className="eval-form" onSubmit={handleSubmit}>
        <div className="eval-form-group">
          <label className="eval-form-label">Run Type</label>
          <select
            className="eval-form-select"
            value={runType}
            onChange={(e) => setRunType(e.target.value as RunType)}
          >
            {RUN_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        {showOD && (
          <div className="eval-form-group">
            <label className="eval-form-label">OD Model</label>
            <select
              className="eval-form-select"
              value={odModelId}
              onChange={(e) => setOdModelId(e.target.value)}
            >
              <option value="">— Select OD model —</option>
              {odModels.map((m) => (
                <option key={m.model_id} value={m.service_model_id}>
                  {m.service_model_id} ({m.deployment_status})
                </option>
              ))}
            </select>
          </div>
        )}

        {showPR && (
          <div className="eval-form-group">
            <label className="eval-form-label">PR Model</label>
            <select
              className="eval-form-select"
              value={prModelId}
              onChange={(e) => setPrModelId(e.target.value)}
            >
              <option value="">— Select PR model —</option>
              {prModels.map((m) => (
                <option key={m.model_id} value={m.service_model_id}>
                  {m.service_model_id} ({m.deployment_status})
                </option>
              ))}
            </select>
          </div>
        )}

        {showPC && (
          <div className="eval-form-group">
            <label className="eval-form-label">PC Model</label>
            <select
              className="eval-form-select"
              value={pcModelId}
              onChange={(e) => setPcModelId(e.target.value)}
            >
              <option value="">— Select PC model —</option>
              {pcModels.map((m) => (
                <option key={m.model_id} value={m.service_model_id}>
                  {m.service_model_id} ({m.deployment_status})
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="eval-form-group">
          <label className="eval-form-label">Dataset</label>
          <select
            className="eval-form-select"
            value={datasetId}
            onChange={(e) => setDatasetId(e.target.value)}
            required
          >
            <option value="">— Select dataset —</option>
            {datasets.map((d) => (
              <option key={d.dataset_id} value={d.dataset_id}>
                {d.dataset_name} {d.status === 'locked' ? '(locked)' : '(draft)'} — {d.sample_count}{' '}
                samples
              </option>
            ))}
          </select>
          {isDraft && (
            <div className="eval-form-warning">
              Warning: selected dataset is in draft status and may have incomplete annotations.
            </div>
          )}
        </div>

        {showBaseRunToggle && datasetId && (
          <div className="eval-form-group">
            <label className="eval-form-label">Input Data Source</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: useBaseRun ? 10 : 0 }}>
              <button
                type="button"
                className={useBaseRun ? 'eval-btn-ghost' : 'eval-btn'}
                style={{ padding: '5px 14px', fontSize: 12 }}
                onClick={() => setUseBaseRun(false)}
              >
                Use GT data
              </button>
              <button
                type="button"
                className={useBaseRun ? 'eval-btn' : 'eval-btn-ghost'}
                style={{ padding: '5px 14px', fontSize: 12 }}
                onClick={() => setUseBaseRun(true)}
              >
                Use base run
              </button>
            </div>
            {useBaseRun &&
              (baseRunsLoading ? (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  Loading eligible runs...
                </div>
              ) : baseRuns.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  No completed base runs available for this dataset.
                </div>
              ) : (
                <select
                  className="eval-form-select"
                  value={baseRunId}
                  onChange={(e) => setBaseRunId(e.target.value)}
                >
                  <option value="">— Select base run —</option>
                  {baseRuns.map((r) => (
                    <option key={r.run_id} value={r.run_id}>
                      {baseRunLabel(r, runType as 'PR' | 'PC')}
                    </option>
                  ))}
                </select>
              ))}
          </div>
        )}

        <div className="eval-form-group">
          <label className="eval-form-label">Config (optional JSON)</label>
          <textarea
            className="eval-form-textarea"
            value={configJson}
            onChange={(e) => setConfigJson(e.target.value)}
            placeholder='{"iou_threshold": 0.5, "confidence_threshold": 0.3}'
          />
        </div>

        <div>
          <button type="submit" className="eval-btn" disabled={submitting || !datasetId}>
            {submitting ? 'Scheduling...' : 'Schedule Run'}
          </button>
        </div>
      </form>
    </div>
  );
};
